import { renderInto } from './render';
import { looksLikeFullHtml } from './render-html';
import { fetchResume, verifySecret } from './api';
import { clearSavedSecret, getSavedSecret } from './auth-storage';
import type { ResumeData } from './types';

export async function loadInitialState(): Promise<ResumeData> {
  const fresh = takeFreshSnapshot();
  if (fresh !== null) {
    return fresh;
  }
  try {
    const fetched = await fetchResume();
    return fetched ?? { content: '', showToc: false, hidden: false, format: 'md' };
  } catch {
    return { content: '', showToc: false, hidden: false, format: 'md' };
  }
}

function takeFreshSnapshot(): ResumeData | null {
  try {
    const raw = sessionStorage.getItem('resume_fresh');
    if (raw === null) {
      return null;
    }
    // 故意 consume-on-read：仅"保存→退出→/"路径用一次。再次刷新会回 KV，
    // 此时 KV 大概率已一致；快照永留会让用户看到的内容永久领先 KV，更糟。
    sessionStorage.removeItem('resume_fresh');
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed === 'object' && parsed !== null) {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.content === 'string' && typeof obj.showToc === 'boolean') {
        const hidden = typeof obj.hidden === 'boolean' ? obj.hidden : false;
        const format = obj.format === 'html' ? 'html' : 'md';
        return { content: obj.content, showToc: obj.showToc, hidden, format };
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export async function maybeMountEditEntry(): Promise<void> {
  const saved = getSavedSecret();
  if (saved === null) {
    return;
  }
  const result = await verifySecret(saved);
  if (result === 'invalid') {
    // 密码确实失效，清掉避免下次进 /edit 时还白尝试一遍
    clearSavedSecret();
    return;
  }
  if (result !== 'ok') {
    // 限流 / 网络异常：无法判定，保留密钥下次刷新再试，仅这次不展示按钮
    return;
  }
  mountEditButton();
}

function mountEditButton(): void {
  if (document.getElementById('view-edit-entry') !== null) {
    return;
  }
  const btn = document.createElement('button');
  btn.id = 'view-edit-entry';
  btn.type = 'button';
  btn.className =
    'fixed right-4 top-4 z-30 inline-flex items-center gap-1.5 rounded-lg bg-[#4f46e5] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#4338ca] active:scale-[0.99] print:hidden';
  btn.innerHTML = '<span aria-hidden="true">✎</span><span>编辑</span>';
  btn.addEventListener('click', () => {
    window.location.href = '/edit';
  });
  document.body.appendChild(btn);
}

function renderEmpty(text: string): string {
  return `
    <div class="empty-state">
      <img class="empty-illustration" src="/2x_error_dog.png" alt="" draggable="false">
      <div class="empty-text">${text}</div>
    </div>
  `;
}

export async function renderViewer(state: ResumeData): Promise<void> {
  const article = document.getElementById('content');
  if (article === null) {
    return;
  }
  // HTML 模式：去掉 markdown-body，挂 html-iframe-host 把 article 撑成 100vh
  // 宿主，iframe 100%×100% 占满，CSS/JS 由 sandbox iframe 完全隔离自控
  if (state.format === 'html') {
    article.classList.remove('markdown-body');
    article.classList.add('html-iframe-host');
  } else {
    article.classList.add('markdown-body');
    article.classList.remove('html-iframe-host');
  }
  if (state.hidden) {
    article.innerHTML = renderEmpty('简历已隐藏');
    article.hidden = false;
    article.removeAttribute('aria-busy');
    return;
  }
  if (state.content.trim() === '') {
    article.innerHTML = renderEmpty('空空如也');
    article.hidden = false;
    article.removeAttribute('aria-busy');
    return;
  }
  // 格式错配兜底：format=md 但内容是整页 HTML 时不渲染（markdown-it+DOMPurify
  // 会把 <html>/<head>/<style> 剥光成空白），改显示明确提示让站主回去修正格式
  if (state.format === 'md' && looksLikeFullHtml(state.content)) {
    article.innerHTML = renderEmpty('简历异常，无法查看');
    article.hidden = false;
    article.removeAttribute('aria-busy');
    return;
  }
  renderInto(article, state.content, state.format);
  article.hidden = false;
  article.removeAttribute('aria-busy');
  if (state.showToc) {
    const { mountToc } = await import('./toc');
    mountToc(article);
  }
}
